import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type EventFamily = 'trivia' | 'judged';
type EventSubtype = 'themed_trivia' | 'office_trivia' | 'cook_off' | 'karaoke_battle';
type RoundType = 'trivia_round' | 'judged_round';
type RuntimeAction =
  | 'start_event'
  | 'open_round'
  | 'present_item'
  | 'reveal_answer'
  | 'score_item'
  | 'next_item'
  | 'next_round'
  | 'finish_event';

type TemplateRow = {
  id: string;
  owner_id: string | null;
  title: string;
  description: string | null;
  activity_family: EventFamily;
  event_subtype: EventSubtype;
  default_team_size: number;
  timer_seconds: number;
  locked_at: string | null;
  is_platform_template: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
};

type RoundRow = {
  id: string;
  template_id: string;
  position: number;
  title: string;
  round_type: RoundType;
  timer_seconds: number | null;
  settings: Record<string, unknown>;
};

type ItemRow = {
  id: string;
  round_id: string;
  position: number;
  prompt: string | null;
  answer: string | null;
  choices: string[];
  point_value: number;
  criterion_label: string | null;
  max_score: number | null;
  rubric: string | null;
  media_url: string | null;
  settings: Record<string, unknown>;
};

type TemplateEditorRound = RoundRow & {
  items: ItemRow[];
};

type HostedEventRow = {
  id: string;
  venue_id: string;
  title: string;
  description: string | null;
  activity_family: EventFamily;
  event_subtype: EventSubtype;
  starts_at: string;
  status: string;
  runtime_status: string;
  current_round_index: number;
  current_item_index: number;
  template_snapshot: {
    rounds: Array<{
      id: string;
      title: string;
      round_type: RoundType;
      timer_seconds: number | null;
      items: Array<{
        id: string;
        prompt: string | null;
        answer: string | null;
        point_value: number;
        criterion_label: string | null;
        max_score: number | null;
        rubric: string | null;
      }>;
    }>;
  };
};

type HostedEventTeam = {
  id: string;
  hosted_event_id: string;
  name: string;
  position: number;
};

type HostedEventStanding = {
  team_id: string;
  team_name: string;
  total_score: number;
  rank: number;
};

type StatusTone = 'info' | 'success' | 'error' | 'warning';
type StatusMessage = { tone: StatusTone; text: string };

function statusPill(status: StatusMessage | null) {
  if (!status) return null;
  return <div className={`admin-status ${status.tone}`}>{status.text}</div>;
}

function emptyItem(roundType: RoundType, position: number): ItemRow {
  return {
    id: `draft-item-${position}-${Math.random().toString(36).slice(2, 8)}`,
    round_id: '',
    position,
    prompt: roundType === 'trivia_round' ? '' : null,
    answer: roundType === 'trivia_round' ? '' : null,
    choices: [],
    point_value: roundType === 'trivia_round' ? 1 : 10,
    criterion_label: roundType === 'judged_round' ? 'Criterion' : null,
    max_score: roundType === 'judged_round' ? 10 : null,
    rubric: '',
    media_url: '',
    settings: {},
  };
}

function emptyRound(position: number, roundType: RoundType): TemplateEditorRound {
  return {
    id: `draft-round-${position}-${Math.random().toString(36).slice(2, 8)}`,
    template_id: '',
    position,
    title: roundType === 'trivia_round' ? `Round ${position}` : 'Judging Round',
    round_type: roundType,
    timer_seconds: roundType === 'trivia_round' ? 30 : 0,
    settings: {},
    items: [emptyItem(roundType, 1)],
  };
}

function normalizeChoices(value: string): string[] {
  return value
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function choicesText(item: ItemRow) {
  return item.choices.join('\n');
}

function parseTemplateSnapshot(value: any): HostedEventRow['template_snapshot'] {
  const rounds = Array.isArray(value?.rounds) ? value.rounds : [];
  return {
    rounds: rounds.map((round: any) => ({
      id: typeof round?.id === 'string' ? round.id : '',
      title: typeof round?.title === 'string' ? round.title : 'Round',
      round_type: round?.round_type === 'judged_round' ? 'judged_round' : 'trivia_round',
      timer_seconds: typeof round?.timer_seconds === 'number' ? round.timer_seconds : null,
      items: Array.isArray(round?.items)
        ? round.items.map((item: any) => ({
            id: typeof item?.id === 'string' ? item.id : '',
            prompt: typeof item?.prompt === 'string' ? item.prompt : null,
            answer: typeof item?.answer === 'string' ? item.answer : null,
            point_value: typeof item?.point_value === 'number' ? item.point_value : Number(item?.point_value ?? 0) || 0,
            criterion_label: typeof item?.criterion_label === 'string' ? item.criterion_label : null,
            max_score: typeof item?.max_score === 'number' ? item.max_score : Number(item?.max_score ?? 0) || null,
            rubric: typeof item?.rubric === 'string' ? item.rubric : null,
          }))
        : [],
    })),
  };
}

function subtypeOptions(family: EventFamily): EventSubtype[] {
  return family === 'judged' ? ['cook_off', 'karaoke_battle'] : ['office_trivia', 'themed_trivia'];
}

export default function EventsWorkspace({ session }: { session: Session }) {
  const initialVenueId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('venueId') ?? '';
    } catch {
      return '';
    }
  }, []);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [templateDraft, setTemplateDraft] = useState<TemplateRow | null>(null);
  const [rounds, setRounds] = useState<TemplateEditorRound[]>([]);
  const [events, setEvents] = useState<HostedEventRow[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventDetail, setEventDetail] = useState<HostedEventRow | null>(null);
  const [eventTeams, setEventTeams] = useState<HostedEventTeam[]>([]);
  const [standings, setStandings] = useState<HostedEventStanding[]>([]);
  const [publishVenueId, setPublishVenueId] = useState(initialVenueId);
  const [publishTitle, setPublishTitle] = useState('');
  const [publishStartsAt, setPublishStartsAt] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [scoreDrafts, setScoreDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    const { data, error } = await supabase
      .from('event_templates')
      .select('*')
      .order('is_platform_template', { ascending: false })
      .order('title');
    if (error) throw error;
    const rows = (data ?? []) as TemplateRow[];
    setTemplates(rows);
    if (!selectedTemplateId && rows[0]?.id) {
      setSelectedTemplateId(rows[0].id);
    }
  }, [selectedTemplateId]);

  const loadTemplateDetail = useCallback(async (templateId: string) => {
    const [{ data: template, error: templateError }, { data: roundsData, error: roundsError }, { data: itemsData, error: itemsError }] = await Promise.all([
      supabase.from('event_templates').select('*').eq('id', templateId).maybeSingle(),
      supabase.from('event_template_rounds').select('*').eq('template_id', templateId).order('position'),
      supabase
        .from('event_template_items')
        .select('*, event_template_rounds!inner(template_id)')
        .eq('event_template_rounds.template_id', templateId)
        .order('position'),
    ]);
    if (templateError) throw templateError;
    if (roundsError) throw roundsError;
    if (itemsError) throw itemsError;
    if (!template) return;

    const itemsByRound = new Map<string, ItemRow[]>();
    ((itemsData ?? []) as any[]).forEach((item) => {
      const parsed: ItemRow = {
        id: String(item.id),
        round_id: String(item.round_id),
        position: Number(item.position ?? 1),
        prompt: typeof item.prompt === 'string' ? item.prompt : '',
        answer: typeof item.answer === 'string' ? item.answer : '',
        choices: Array.isArray(item.choices) ? item.choices.filter((value: unknown) => typeof value === 'string') : [],
        point_value: Number(item.point_value ?? 0) || 0,
        criterion_label: typeof item.criterion_label === 'string' ? item.criterion_label : '',
        max_score: item.max_score == null ? null : Number(item.max_score),
        rubric: typeof item.rubric === 'string' ? item.rubric : '',
        media_url: typeof item.media_url === 'string' ? item.media_url : '',
        settings: item.settings && typeof item.settings === 'object' ? item.settings : {},
      };
      itemsByRound.set(parsed.round_id, [...(itemsByRound.get(parsed.round_id) ?? []), parsed]);
    });

    const nextRounds = ((roundsData ?? []) as RoundRow[]).map((round) => ({
      ...round,
      settings: round.settings && typeof round.settings === 'object' ? round.settings : {},
      items: (itemsByRound.get(round.id) ?? []).sort((a, b) => a.position - b.position),
    }));

    setTemplateDraft(template as TemplateRow);
    setRounds(nextRounds);
    setPublishTitle(template.title);
  }, []);

  const loadEvents = useCallback(async () => {
    const { data, error } = await supabase
      .from('hosted_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(40);
    if (error) throw error;
    const rows = ((data ?? []) as any[]).map((row) => ({
      ...(row as HostedEventRow),
      template_snapshot: parseTemplateSnapshot(row.template_snapshot),
    }));
    setEvents(rows);
    if (!selectedEventId && rows[0]?.id) {
      setSelectedEventId(rows[0].id);
    }
  }, [selectedEventId]);

  const loadEventDetail = useCallback(async (eventId: string) => {
    const [{ data: event, error: eventError }, { data: teams, error: teamsError }, { data: standingsData, error: standingsError }] = await Promise.all([
      supabase.from('hosted_events').select('*').eq('id', eventId).maybeSingle(),
      supabase.from('hosted_event_teams').select('*').eq('hosted_event_id', eventId).order('position'),
      supabase.rpc('get_hosted_event_standings_v1', { p_event_id: eventId }),
    ]);
    if (eventError) throw eventError;
    if (teamsError) throw teamsError;
    if (standingsError) throw standingsError;
    setEventDetail(event ? { ...(event as HostedEventRow), template_snapshot: parseTemplateSnapshot((event as any).template_snapshot) } : null);
    setEventTeams((teams ?? []) as HostedEventTeam[]);
    setStandings(
      ((standingsData ?? []) as any[]).map((row) => ({
        team_id: String(row.team_id),
        team_name: String(row.team_name),
        total_score: Number(row.total_score ?? 0) || 0,
        rank: Number(row.rank ?? 0) || 0,
      })),
    );
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await Promise.all([loadTemplates(), loadEvents()]);
      } catch (error) {
        setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to load hosted events.' });
      } finally {
        setLoading(false);
      }
    })();
  }, [loadEvents, loadTemplates]);

  useEffect(() => {
    if (!selectedTemplateId) return;
    void loadTemplateDetail(selectedTemplateId).catch((error) => {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to load template.' });
    });
  }, [loadTemplateDetail, selectedTemplateId]);

  useEffect(() => {
    if (!selectedEventId) return;
    void loadEventDetail(selectedEventId).catch((error) => {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to load event.' });
    });
  }, [loadEventDetail, selectedEventId]);

  const currentRound = useMemo(() => {
    if (!eventDetail) return null;
    return eventDetail.template_snapshot.rounds[eventDetail.current_round_index] ?? null;
  }, [eventDetail]);

  const currentItem = useMemo(() => {
    if (!currentRound || !eventDetail) return null;
    return currentRound.items[eventDetail.current_item_index] ?? null;
  }, [currentRound, eventDetail]);

  const createTemplate = useCallback(async () => {
    try {
      setStatus({ tone: 'info', text: 'Creating template…' });
      const { data, error } = await supabase
        .from('event_templates')
        .insert({
          owner_id: session.user.id,
          title: 'Untitled Hosted Event',
          description: '',
          activity_family: 'trivia',
          event_subtype: 'office_trivia',
          default_team_size: 4,
          timer_seconds: 30,
          scoring_model: 'sum',
          lock_rules: {},
          is_public: false,
        })
        .select('*')
        .single();
      if (error) throw error;
      const templateId = String(data.id);
      await supabase.from('event_template_rounds').insert({
        template_id: templateId,
        position: 1,
        title: 'Round 1',
        round_type: 'trivia_round',
        timer_seconds: 30,
        settings: {},
      });
      await loadTemplates();
      setSelectedTemplateId(templateId);
      setStatus({ tone: 'success', text: 'Template created.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to create template.' });
    }
  }, [loadTemplates, session.user.id]);

  const saveTemplate = useCallback(async () => {
    if (!templateDraft) return;
    try {
      setStatus({ tone: 'info', text: 'Saving template…' });
      const draft = {
        ...templateDraft,
        event_subtype: subtypeOptions(templateDraft.activity_family).includes(templateDraft.event_subtype)
          ? templateDraft.event_subtype
          : subtypeOptions(templateDraft.activity_family)[0],
      };
      const { error: templateError } = await supabase
        .from('event_templates')
        .update({
          title: draft.title,
          description: draft.description,
          activity_family: draft.activity_family,
          event_subtype: draft.event_subtype,
          default_team_size: draft.default_team_size,
          timer_seconds: draft.timer_seconds,
          is_public: draft.is_public,
        })
        .eq('id', draft.id);
      if (templateError) throw templateError;

      const roundIds = rounds.map((round) => round.id).filter((id) => !id.startsWith('draft-'));
      if (roundIds.length > 0) {
        await supabase.from('event_template_items').delete().in('round_id', roundIds);
      }
      await supabase.from('event_template_rounds').delete().eq('template_id', draft.id);

      for (const [roundIndex, round] of rounds.entries()) {
        const { data: savedRound, error: roundError } = await supabase
          .from('event_template_rounds')
          .insert({
            template_id: draft.id,
            position: roundIndex + 1,
            title: round.title,
            round_type: round.round_type,
            timer_seconds: round.timer_seconds ?? 0,
            settings: round.settings ?? {},
          })
          .select('*')
          .single();
        if (roundError) throw roundError;

        for (const [itemIndex, item] of round.items.entries()) {
          const payload =
            round.round_type === 'trivia_round'
              ? {
                  round_id: savedRound.id,
                  position: itemIndex + 1,
                  prompt: item.prompt,
                  answer: item.answer,
                  choices: item.choices,
                  point_value: item.point_value,
                  rubric: item.rubric,
                  media_url: item.media_url,
                  settings: item.settings ?? {},
                }
              : {
                  round_id: savedRound.id,
                  position: itemIndex + 1,
                  criterion_label: item.criterion_label,
                  max_score: item.max_score ?? item.point_value,
                  point_value: item.point_value,
                  rubric: item.rubric,
                  media_url: item.media_url,
                  settings: item.settings ?? {},
                };
          const { error: itemError } = await supabase.from('event_template_items').insert(payload);
          if (itemError) throw itemError;
        }
      }
      await loadTemplates();
      await loadTemplateDetail(draft.id);
      setStatus({ tone: 'success', text: 'Template saved.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to save template.' });
    }
  }, [loadTemplateDetail, loadTemplates, rounds, templateDraft]);

  const duplicateTemplate = useCallback(async () => {
    if (!templateDraft) return;
    try {
      setStatus({ tone: 'info', text: 'Duplicating template…' });
      const { data, error } = await supabase
        .from('event_templates')
        .insert({
          owner_id: session.user.id,
          title: `${templateDraft.title} Copy`,
          description: templateDraft.description,
          activity_family: templateDraft.activity_family,
          event_subtype: templateDraft.event_subtype,
          default_team_size: templateDraft.default_team_size,
          timer_seconds: templateDraft.timer_seconds,
          scoring_model: 'sum',
          lock_rules: {},
          is_public: false,
        })
        .select('*')
        .single();
      if (error) throw error;
      const newTemplateId = String(data.id);
      for (const [roundIndex, round] of rounds.entries()) {
        const { data: savedRound, error: roundError } = await supabase
          .from('event_template_rounds')
          .insert({
            template_id: newTemplateId,
            position: roundIndex + 1,
            title: round.title,
            round_type: round.round_type,
            timer_seconds: round.timer_seconds ?? 0,
            settings: round.settings ?? {},
          })
          .select('*')
          .single();
        if (roundError) throw roundError;
        for (const [itemIndex, item] of round.items.entries()) {
          const payload =
            round.round_type === 'trivia_round'
              ? {
                  round_id: savedRound.id,
                  position: itemIndex + 1,
                  prompt: item.prompt,
                  answer: item.answer,
                  choices: item.choices,
                  point_value: item.point_value,
                  rubric: item.rubric,
                  media_url: item.media_url,
                  settings: item.settings ?? {},
                }
              : {
                  round_id: savedRound.id,
                  position: itemIndex + 1,
                  criterion_label: item.criterion_label,
                  max_score: item.max_score ?? item.point_value,
                  point_value: item.point_value,
                  rubric: item.rubric,
                  media_url: item.media_url,
                  settings: item.settings ?? {},
                };
          const { error: itemError } = await supabase.from('event_template_items').insert(payload);
          if (itemError) throw itemError;
        }
      }
      await loadTemplates();
      setSelectedTemplateId(newTemplateId);
      setStatus({ tone: 'success', text: 'Template duplicated.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to duplicate template.' });
    }
  }, [loadTemplates, rounds, session.user.id, templateDraft]);

  const lockTemplate = useCallback(async () => {
    if (!templateDraft) return;
    try {
      setStatus({ tone: 'info', text: 'Locking template…' });
      const { error } = await supabase
        .from('event_templates')
        .update({ locked_at: new Date().toISOString(), published_at: new Date().toISOString(), is_public: true })
        .eq('id', templateDraft.id);
      if (error) throw error;
      await loadTemplates();
      await loadTemplateDetail(templateDraft.id);
      setStatus({ tone: 'success', text: 'Template locked and ready to publish.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to lock template.' });
    }
  }, [loadTemplateDetail, loadTemplates, templateDraft]);

  const publishEvent = useCallback(async () => {
    if (!templateDraft || !publishVenueId.trim()) return;
    try {
      setStatus({ tone: 'info', text: 'Publishing hosted event…' });
      const { data, error } = await supabase.rpc('create_hosted_event_from_template_v1', {
        p_template_id: templateDraft.id,
        p_venue_id: publishVenueId.trim(),
        p_starts_at: publishStartsAt ? new Date(publishStartsAt).toISOString() : null,
        p_title: publishTitle.trim() || templateDraft.title,
      });
      if (error) throw error;
      await loadEvents();
      const eventId = String((data as any)?.id ?? '');
      if (eventId) setSelectedEventId(eventId);
      setStatus({ tone: 'success', text: 'Hosted event published.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to publish event.' });
    }
  }, [loadEvents, publishStartsAt, publishTitle, publishVenueId, templateDraft]);

  const addRound = useCallback((roundType?: RoundType) => {
    setRounds((current) => [...current, emptyRound(current.length + 1, roundType ?? (templateDraft?.activity_family === 'judged' ? 'judged_round' : 'trivia_round'))]);
  }, [templateDraft?.activity_family]);

  const addItem = useCallback((roundId: string) => {
    setRounds((current) =>
      current.map((round) =>
        round.id === roundId
          ? { ...round, items: [...round.items, emptyItem(round.round_type, round.items.length + 1)] }
          : round,
      ),
    );
  }, []);

  const createTeam = useCallback(async () => {
    if (!selectedEventId || !newTeamName.trim()) return;
    try {
      setStatus({ tone: 'info', text: 'Adding team…' });
      const { error } = await supabase.from('hosted_event_teams').insert({
        hosted_event_id: selectedEventId,
        name: newTeamName.trim(),
        position: eventTeams.length + 1,
        created_by: session.user.id,
      });
      if (error) throw error;
      setNewTeamName('');
      await loadEventDetail(selectedEventId);
      setStatus({ tone: 'success', text: 'Team added.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to add team.' });
    }
  }, [eventTeams.length, loadEventDetail, newTeamName, selectedEventId, session.user.id]);

  const runAction = useCallback(async (action: RuntimeAction) => {
    if (!selectedEventId) return;
    try {
      setStatus({ tone: 'info', text: `Running ${action.replace('_', ' ')}…` });
      const { error } = await supabase.rpc('hosted_event_runtime_action_v1', {
        p_event_id: selectedEventId,
        p_action: action,
      });
      if (error) throw error;
      await Promise.all([loadEventDetail(selectedEventId), loadEvents()]);
      setStatus({ tone: 'success', text: 'Runtime updated.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Runtime action failed.' });
    }
  }, [loadEventDetail, loadEvents, selectedEventId]);

  const saveScore = useCallback(async (teamId: string) => {
    if (!selectedEventId || !currentRound || !currentItem) return;
    const raw = scoreDrafts[teamId] ?? '';
    const score = Number(raw);
    if (!Number.isFinite(score)) {
      setStatus({ tone: 'warning', text: 'Enter a numeric score first.' });
      return;
    }
    try {
      setStatus({ tone: 'info', text: 'Saving score…' });
      const { error } = await supabase.rpc('hosted_event_upsert_score_v1', {
        p_event_id: selectedEventId,
        p_team_id: teamId,
        p_round_id: currentRound.id,
        p_item_id: currentItem.id,
        p_score: score,
        p_note: null,
        p_metadata: {},
      });
      if (error) throw error;
      await loadEventDetail(selectedEventId);
      setStatus({ tone: 'success', text: 'Score saved.' });
    } catch (error) {
      setStatus({ tone: 'error', text: error instanceof Error ? error.message : 'Failed to save score.' });
    }
  }, [currentItem, currentRound, loadEventDetail, scoreDrafts, selectedEventId]);

  const templateLocked = Boolean(templateDraft?.locked_at);

  return (
    <div className="admin-grid" style={{ gap: 20 }}>
      <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div className="admin-title">Hosted Events</div>
          <div className="admin-subtitle">
            Build venue-first trivia and judged challenge packs, then publish and run them live.
          </div>
        </div>
        {statusPill(status)}
        {loading ? <div className="admin-muted">Loading hosted events…</div> : null}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) minmax(0, 1fr)', gap: 20 }}>
        <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div>
              <div className="admin-title" style={{ fontSize: 18 }}>Templates</div>
              <div className="admin-subtitle">Platform packs and your editable copies.</div>
            </div>
            <button className="admin-btn" type="button" onClick={createTemplate}>New</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 560, overflow: 'auto' }}>
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                className={`admin-list-row ${selectedTemplateId === template.id ? 'active' : ''}`}
                onClick={() => setSelectedTemplateId(template.id)}
                style={{ textAlign: 'left' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong>{template.title}</strong>
                  <span className="admin-muted">{template.locked_at ? 'Locked' : 'Draft'}</span>
                </div>
                <div className="admin-muted" style={{ marginTop: 4 }}>
                  {template.activity_family} · {template.event_subtype.replace('_', ' ')}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gap: 20 }}>
          <div className="admin-card" style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div className="admin-title" style={{ fontSize: 18 }}>Template Editor</div>
                <div className="admin-subtitle">Desktop-first pack authoring for trivia and judged challenges.</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button className="admin-btn" type="button" onClick={duplicateTemplate} disabled={!templateDraft}>Duplicate</button>
                <button className="admin-btn" type="button" onClick={saveTemplate} disabled={!templateDraft || templateLocked}>Save</button>
                <button className="admin-btn primary" type="button" onClick={lockTemplate} disabled={!templateDraft || templateLocked}>Lock</button>
              </div>
            </div>

            {templateDraft ? (
              <>
                <div className="admin-tip-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                  <label className="admin-label">
                    Title
                    <input className="admin-input" value={templateDraft.title} disabled={templateLocked} onChange={(e) => setTemplateDraft((current) => current ? { ...current, title: e.target.value } : current)} />
                  </label>
                  <label className="admin-label">
                    Subtitle / description
                    <input className="admin-input" value={templateDraft.description ?? ''} disabled={templateLocked} onChange={(e) => setTemplateDraft((current) => current ? { ...current, description: e.target.value } : current)} />
                  </label>
                  <label className="admin-label">
                    Family
                    <select
                      className="admin-input"
                      value={templateDraft.activity_family}
                      disabled={templateLocked}
                      onChange={(e) => {
                        const family = e.target.value as EventFamily;
                        setTemplateDraft((current) => current ? { ...current, activity_family: family, event_subtype: subtypeOptions(family)[0] } : current);
                        setRounds((current) =>
                          current.map((round) => ({
                            ...round,
                            round_type: family === 'judged' ? 'judged_round' : 'trivia_round',
                            items: round.items.map((item, itemIndex) => ({
                              ...emptyItem(family === 'judged' ? 'judged_round' : 'trivia_round', itemIndex + 1),
                              id: item.id,
                            })),
                          })),
                        );
                      }}
                    >
                      <option value="trivia">Trivia</option>
                      <option value="judged">Judged challenge</option>
                    </select>
                  </label>
                  <label className="admin-label">
                    Subtype
                    <select className="admin-input" value={templateDraft.event_subtype} disabled={templateLocked} onChange={(e) => setTemplateDraft((current) => current ? { ...current, event_subtype: e.target.value as EventSubtype } : current)}>
                      {subtypeOptions(templateDraft.activity_family).map((option) => (
                        <option key={option} value={option}>{option.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-label">
                    Default team size
                    <input className="admin-input" type="number" min={1} max={32} value={templateDraft.default_team_size} disabled={templateLocked} onChange={(e) => setTemplateDraft((current) => current ? { ...current, default_team_size: Number(e.target.value) || 1 } : current)} />
                  </label>
                  <label className="admin-label">
                    Default timer (seconds)
                    <input className="admin-input" type="number" min={0} max={3600} value={templateDraft.timer_seconds} disabled={templateLocked} onChange={(e) => setTemplateDraft((current) => current ? { ...current, timer_seconds: Number(e.target.value) || 0 } : current)} />
                  </label>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div>
                    <div className="admin-title" style={{ fontSize: 16 }}>Rounds</div>
                    <div className="admin-subtitle">Questions or judging criteria are stored as ordered items.</div>
                  </div>
                  <button className="admin-btn" type="button" onClick={() => addRound()} disabled={templateLocked}>Add round</button>
                </div>

                <div style={{ display: 'grid', gap: 16 }}>
                  {rounds.map((round, roundIndex) => (
                    <div key={round.id} className="admin-card" style={{ background: 'rgba(255,255,255,0.03)', display: 'grid', gap: 12 }}>
                      <div className="admin-tip-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr auto' }}>
                        <label className="admin-label">
                          Round title
                          <input className="admin-input" value={round.title} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, title: e.target.value } : entry))} />
                        </label>
                        <label className="admin-label">
                          Type
                          <select className="admin-input" value={round.round_type} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, round_type: e.target.value as RoundType } : entry))}>
                            <option value="trivia_round">Trivia round</option>
                            <option value="judged_round">Judged round</option>
                          </select>
                        </label>
                        <label className="admin-label">
                          Timer
                          <input className="admin-input" type="number" min={0} max={3600} value={round.timer_seconds ?? 0} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, timer_seconds: Number(e.target.value) || 0 } : entry))} />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                          <button className="admin-btn" type="button" disabled={templateLocked} onClick={() => addItem(round.id)}>Add item</button>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 10 }}>
                        {round.items.map((item) => (
                          <div key={item.id} className="admin-card" style={{ background: 'rgba(255,255,255,0.02)', display: 'grid', gap: 10 }}>
                            {round.round_type === 'trivia_round' ? (
                              <>
                                <label className="admin-label">
                                  Prompt
                                  <textarea className="admin-input" value={item.prompt ?? ''} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, prompt: e.target.value } : candidate) } : entry))} rows={3} />
                                </label>
                                <div className="admin-tip-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                                  <label className="admin-label">
                                    Answer
                                    <input className="admin-input" value={item.answer ?? ''} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, answer: e.target.value } : candidate) } : entry))} />
                                  </label>
                                  <label className="admin-label">
                                    Points
                                    <input className="admin-input" type="number" min={0} max={100} value={item.point_value} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, point_value: Number(e.target.value) || 0 } : candidate) } : entry))} />
                                  </label>
                                  <label className="admin-label">
                                    Media URL
                                    <input className="admin-input" value={item.media_url ?? ''} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, media_url: e.target.value } : candidate) } : entry))} />
                                  </label>
                                </div>
                                <label className="admin-label">
                                  Choices (one per line)
                                  <textarea className="admin-input" value={choicesText(item)} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, choices: normalizeChoices(e.target.value) } : candidate) } : entry))} rows={4} />
                                </label>
                              </>
                            ) : (
                              <>
                                <div className="admin-tip-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                                  <label className="admin-label">
                                    Criterion
                                    <input className="admin-input" value={item.criterion_label ?? ''} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, criterion_label: e.target.value } : candidate) } : entry))} />
                                  </label>
                                  <label className="admin-label">
                                    Max score
                                    <input className="admin-input" type="number" min={0} max={100} value={item.max_score ?? item.point_value} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, max_score: Number(e.target.value) || 0, point_value: Number(e.target.value) || 0 } : candidate) } : entry))} />
                                  </label>
                                  <label className="admin-label">
                                    Media URL
                                    <input className="admin-input" value={item.media_url ?? ''} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, media_url: e.target.value } : candidate) } : entry))} />
                                  </label>
                                </div>
                                <label className="admin-label">
                                  Rubric
                                  <textarea className="admin-input" value={item.rubric ?? ''} disabled={templateLocked} onChange={(e) => setRounds((current) => current.map((entry) => entry.id === round.id ? { ...entry, items: entry.items.map((candidate) => candidate.id === item.id ? { ...candidate, rubric: e.target.value } : candidate) } : entry))} rows={3} />
                                </label>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="admin-empty">Select a template to edit.</div>
            )}
          </div>

          <div className="admin-card" style={{ display: 'grid', gap: 16 }}>
            <div>
              <div className="admin-title" style={{ fontSize: 18 }}>Publish + Runtime</div>
              <div className="admin-subtitle">Create a venue event from a locked template, add teams, and run it live.</div>
            </div>
            <div className="admin-tip-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              <label className="admin-label">
                Venue ID
                <input className="admin-input" value={publishVenueId} onChange={(e) => setPublishVenueId(e.target.value)} placeholder="UUID from venue" />
              </label>
              <label className="admin-label">
                Event title
                <input className="admin-input" value={publishTitle} onChange={(e) => setPublishTitle(e.target.value)} placeholder="Override optional" />
              </label>
              <label className="admin-label">
                Starts at
                <input className="admin-input" type="datetime-local" value={publishStartsAt} onChange={(e) => setPublishStartsAt(e.target.value)} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="admin-btn primary" type="button" onClick={publishEvent} disabled={!templateDraft || !templateLocked || !publishVenueId.trim()}>
                Publish hosted event
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflow: 'auto' }}>
                {events.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    className={`admin-list-row ${selectedEventId === event.id ? 'active' : ''}`}
                    onClick={() => setSelectedEventId(event.id)}
                    style={{ textAlign: 'left' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <strong>{event.title}</strong>
                      <span className="admin-muted">{event.status}</span>
                    </div>
                    <div className="admin-muted" style={{ marginTop: 4 }}>
                      {event.activity_family} · venue {event.venue_id.slice(0, 8)}
                    </div>
                  </button>
                ))}
              </div>

              {eventDetail ? (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div className="admin-card" style={{ background: 'rgba(255,255,255,0.03)', display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                      <div>
                        <div className="admin-title" style={{ fontSize: 16 }}>{eventDetail.title}</div>
                        <div className="admin-subtitle">
                          {eventDetail.activity_family} · {eventDetail.event_subtype.replace(/_/g, ' ')} · {eventDetail.runtime_status}
                        </div>
                      </div>
                      <div className="admin-muted">{new Date(eventDetail.starts_at).toLocaleString()}</div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="admin-btn" type="button" onClick={() => runAction('start_event')}>Start</button>
                      <button className="admin-btn" type="button" onClick={() => runAction('open_round')}>Open round</button>
                      <button className="admin-btn" type="button" onClick={() => runAction('present_item')}>Present item</button>
                      <button className="admin-btn" type="button" onClick={() => runAction('reveal_answer')}>Reveal</button>
                      <button className="admin-btn" type="button" onClick={() => runAction('score_item')}>Score</button>
                      <button className="admin-btn" type="button" onClick={() => runAction('next_item')}>Next item</button>
                      <button className="admin-btn" type="button" onClick={() => runAction('next_round')}>Next round</button>
                      <button className="admin-btn danger" type="button" onClick={() => runAction('finish_event')}>Finish</button>
                    </div>

                    {currentRound ? (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div className="admin-title" style={{ fontSize: 15 }}>
                          Live round: {currentRound.title}
                        </div>
                        <div className="admin-muted">
                          {currentRound.round_type === 'trivia_round' ? 'Trivia runtime' : 'Judged challenge runtime'}
                        </div>
                        {currentItem ? (
                          <div className="admin-card" style={{ background: 'rgba(255,255,255,0.02)', display: 'grid', gap: 8 }}>
                            <div className="admin-title" style={{ fontSize: 15 }}>
                              {currentItem.prompt || currentItem.criterion_label || 'Current item'}
                            </div>
                            {currentItem.rubric ? <div className="admin-muted">{currentItem.rubric}</div> : null}
                            {currentItem.answer ? <div className="admin-muted">Answer: {currentItem.answer}</div> : null}
                          </div>
                        ) : (
                          <div className="admin-empty">This round has no items yet.</div>
                        )}
                      </div>
                    ) : (
                      <div className="admin-empty">No rounds in the frozen snapshot.</div>
                    )}
                  </div>

                  <div className="admin-card" style={{ background: 'rgba(255,255,255,0.03)', display: 'grid', gap: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                      <div>
                        <div className="admin-title" style={{ fontSize: 16 }}>Teams</div>
                        <div className="admin-subtitle">Create teams before players join from mobile.</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="admin-input" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder="Team name" />
                        <button className="admin-btn" type="button" onClick={createTeam}>Add</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {eventTeams.map((team) => (
                        <div key={team.id} className="admin-list-row" style={{ display: 'grid', gap: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <strong>{team.name}</strong>
                            <span className="admin-muted">Position {team.position}</span>
                          </div>
                          {currentItem ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                className="admin-input"
                                type="number"
                                min={0}
                                step="0.5"
                                value={scoreDrafts[team.id] ?? ''}
                                onChange={(e) => setScoreDrafts((current) => ({ ...current, [team.id]: e.target.value }))}
                                placeholder={currentRound?.round_type === 'trivia_round' ? String(currentItem.point_value) : String(currentItem.max_score ?? currentItem.point_value)}
                              />
                              <button className="admin-btn" type="button" onClick={() => saveScore(team.id)}>Save score</button>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="admin-card" style={{ background: 'rgba(255,255,255,0.03)', display: 'grid', gap: 12 }}>
                    <div>
                      <div className="admin-title" style={{ fontSize: 16 }}>Standings</div>
                      <div className="admin-subtitle">Event-scoped totals only. These do not touch sport leaderboards.</div>
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {standings.map((standing) => (
                        <div key={standing.team_id} className="admin-list-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <div>
                            <strong>#{standing.rank} {standing.team_name}</strong>
                          </div>
                          <div className="admin-muted">{standing.total_score.toFixed(2)} pts</div>
                        </div>
                      ))}
                      {standings.length === 0 ? <div className="admin-empty">No scores posted yet.</div> : null}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="admin-empty">Select a hosted event to run it.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
